import { useCallback, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { usePricingTypes, useCreatePricingType, useUpdatePricingType, useDeletePricingType, type PricingType } from "@/hooks/usePricingTypes";
import { OnlineOrderingCard } from "@/components/settings/OnlineOrderingCard";
import { SquareCustomerAutosyncSetting } from "@/components/settings/SquareCustomerAutosyncSetting";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { RefreshCw, CheckCircle2, XCircle, Loader2, AlertTriangle, Download, Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { captureError } from "@/lib/error-message";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";

function useAppSetting(key: string) {
  return useQuery({
    queryKey: ["app_settings", key],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("value")
        .eq("key", key)
        .single();
      if (error) throw error;
      return data.value as string;
    },
  });
}

function useUpdateSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from("app_settings")
        .update({ value, updated_at: new Date().toISOString() })
        .eq("key", key);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["app_settings", vars.key] });
      toast.success("Setting updated");
    },
    onError: (e: Error) => toast.error(e.message),
});
}

function useCompanySettings() {
  const keys = ["company_name", "company_phone", "company_email", "company_address"];
  return useQuery({
    queryKey: ["app_settings", "company"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", keys);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((s) => { map[s.key] = s.value; });
      return map;
    },
  });
}

function usePaymentSettings() {
  const keys = [
    "etransfer_email", "default_deposit_percent", "etransfer_instructions",
    "default_gratuity_percent", "cc_surcharge_enabled", "cc_surcharge_percent",
  ];
  return useQuery({
    queryKey: ["app_settings", "payment"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", keys);
      if (error) throw error;
      const map: Record<string, string> = {};
      (data ?? []).forEach((s) => { map[s.key] = s.value; });
      return map;
    },
  });
}

function CompanyContactCard() {
  const { data: settings, isLoading } = useCompanySettings();
  const updateSetting = useUpdateSetting();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  if (settings && !initialized) {
    setForm(settings);
    setInitialized(true);
  }

  const handleSave = async () => {
    const entries = Object.entries(form);
    for (const [key, value] of entries) {
      await updateSetting.mutateAsync({ key, value });
    }
    qc.invalidateQueries({ queryKey: ["app_settings", "company"] });
    toast.success("Company contact info saved");
  };

  const fields = [
    { key: "company_name", label: "Company Name" },
    { key: "company_phone", label: "Phone" },
    { key: "company_email", label: "Email" },
    { key: "company_address", label: "Address" },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Quote Contact Information</CardTitle>
        <CardDescription>
          This information appears on customer-facing quotes. Update it here so you don't have to touch code.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : (
          <>
            {fields.map((f) => (
              <div key={f.key} className="space-y-1.5">
                <Label className="text-sm font-medium">{f.label}</Label>
                <Input
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </div>
            ))}
            <Button onClick={handleSave} disabled={updateSetting.isPending}>
              {updateSetting.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Contact Info
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
const SettingsPage = () => {
  const { data: squareEnv, isLoading: envLoading } = useAppSetting("square_environment");
  const updateSetting = useUpdateSetting();
  const autosync = useAppSetting("square_customer_autosync");
  const updateAutosync = useUpdateSetting();
  const { mutate: saveAutosync } = updateAutosync;
  const handleAutosyncChange = useCallback(
    (next: boolean) => saveAutosync({ key: "square_customer_autosync", value: next ? "true" : "false" }),
    [saveAutosync],
  );

  const [testResult, setTestResult] = useState<{ connected: boolean; location_name?: string; error?: string } | null>(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ categories_synced: number; items_synced: number } | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ categories_imported: number; items_imported: number } | null>(null);
  const [pullingInvoices, setPullingInvoices] = useState(false);
  const [pullResult, setPullResult] = useState<{ invoices_imported: number; payments_imported: number } | null>(null);

  const isProduction = squareEnv === "production";

  const handleToggleEnv = async () => {
    const newEnv = isProduction ? "sandbox" : "production";
    await updateSetting.mutateAsync({ key: "square_environment", value: newEnv });
    setTestResult(null);
    setSyncResult(null);
    setImportResult(null);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("square-catalog", {
        body: { action: "test-connection" },
      });
      if (error) throw error;
      setTestResult(data);
    } catch (err) {
      const msg = await edgeFunctionErrorMessage(err, "Connection test failed");
      captureError(err, "square_test_connection", msg);
      setTestResult({ connected: false, error: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("square-catalog", {
        body: { action: "sync-all" },
      });
      if (error) throw error;
      setSyncResult(data);
      toast.success(`Synced ${data.categories_synced} categories and ${data.items_synced} items to Square`);
    } catch (err) {
      const msg = await edgeFunctionErrorMessage(err, "Square sync failed");
      captureError(err, "square_catalog_sync", msg);
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("square-catalog", {
        body: { action: "pull-catalog" },
      });
      if (error) throw error;
      setImportResult(data);
      toast.success(`Imported ${data.categories_imported} categories and ${data.items_imported} items from Square`);
    } catch (err) {
      const msg = await edgeFunctionErrorMessage(err, "Square import failed");
      captureError(err, "square_catalog_import", msg);
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <DashboardLayout title="Settings">
      <div className="max-w-2xl space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Square Integration</CardTitle>
            <CardDescription>
              Manage your Square connection for catalog sync and payments.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Environment Toggle */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Environment</Label>
              {isProduction && (
                <div className="flex items-center gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm">
                  <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                  <span>Production mode — changes affect live Square data.</span>
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className={`text-sm ${!isProduction ? "font-medium" : "text-muted-foreground"}`}>Sandbox</span>
                {envLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Switch checked={isProduction} />
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Switch to {isProduction ? "Sandbox" : "Production"}?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {isProduction
                            ? "Switching to Sandbox will use test credentials. No live data will be affected."
                            : "Switching to Production will use live Square credentials. Any sync or payment actions will affect real data."}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleToggleEnv}>
                          Switch to {isProduction ? "Sandbox" : "Production"}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                <span className={`text-sm ${isProduction ? "font-medium" : "text-muted-foreground"}`}>Production</span>
                <Badge variant={isProduction ? "destructive" : "secondary"} className="ml-2">
                  {isProduction ? "LIVE" : "TEST"}
                </Badge>
              </div>
            </div>

            <Separator />

            {/* Connection Test */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Connection Test</Label>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
                  {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Test Connection
                </Button>
                {testResult && (
                  <div className="flex items-center gap-2 text-sm">
                    {testResult.connected ? (
                      <>
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                        <span>Connected to {testResult.location_name}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 text-destructive" />
                        <span className="text-destructive">{testResult.error}</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            <Separator />

            <SquareCustomerAutosyncSetting
              enabled={autosync.data === "true"}
              isLoading={autosync.isLoading}
              isError={autosync.isError}
              isSaving={updateAutosync.isPending}
              onChange={handleAutosyncChange}
            />

            <Separator />

            {/* Catalog Sync (Push) */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Push to Square</Label>
              <p className="text-sm text-muted-foreground">
                Push all active menu categories and items to your Square catalog.
              </p>
              <div className="flex items-center gap-3">
                <Button onClick={handleSync} disabled={syncing}>
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sync Menu to Square
                </Button>
                {syncResult && (
                  <span className="text-sm text-muted-foreground">
                    ✓ {syncResult.categories_synced} categories, {syncResult.items_synced} items synced
                  </span>
                )}
              </div>
            </div>

            <Separator />

            {/* Import from Square (Pull) */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Import from Square</Label>
              <p className="text-sm text-muted-foreground">
                Pull catalog items from Square into your local menu. New items will be created; existing linked items will be updated.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={importing}>
                      {importing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Import from Square
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Import from Square?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will create new local menu items for any Square catalog objects not already linked. Existing linked items will be updated with data from Square. Prices will be replaced with Square variation data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleImport}>
                        Import
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {importResult && (
                  <span className="text-sm text-muted-foreground">
                    ✓ {importResult.categories_imported} categories, {importResult.items_imported} items imported
                  </span>
                )}
              </div>
            </div>
            <Separator />

            {/* Import Invoices from Square */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Import Invoices from Square</Label>
              <p className="text-sm text-muted-foreground">
                Pull all invoices and orders from Square into Frankly Catering as quotes, including payment history.
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" disabled={pullingInvoices}>
                      {pullingInvoices ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      Import Invoices
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Import Invoices from Square?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will fetch all invoices from your Square account and create corresponding quotes in Frankly Catering. Existing imports will be skipped (idempotent). Payment history will also be imported.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={async () => {
                        setPullingInvoices(true);
                        setPullResult(null);
                        try {
                          const { data, error } = await supabase.functions.invoke("square-payments", {
                            body: { action: "pull-invoices" },
                          });
                          if (error) throw error;
                          setPullResult(data);
                          const warnCount = data.warnings?.length ?? 0;
                          const errCount = data.errors?.length ?? 0;
                          if (errCount > 0 || warnCount > 0) {
                            toast.warning(
                              `Imported ${data.invoices_imported} invoices (${data.payments_imported} payments). ` +
                              `${warnCount} warning${warnCount === 1 ? "" : "s"}` +
                              (errCount > 0 ? `, ${errCount} error${errCount === 1 ? "" : "s"}` : "") +
                              ` — check browser console for details.`
                            );
                            if (warnCount > 0) console.warn("Square pull warnings:", data.warnings);
                            if (errCount > 0) console.error("Square pull errors:", data.errors);
                          } else {
                            toast.success(`Imported ${data.invoices_imported} invoices and ${data.payments_imported} payments from Square`);
                          }
                        } catch (err) {
                          const msg = await edgeFunctionErrorMessage(err, "Invoice import failed");
                          captureError(err, "square_invoice_import", msg);
                          toast.error(msg);
                        } finally {
                          setPullingInvoices(false);
                        }
                      }}>
                        Import
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {pullResult && (
                  <span className="text-sm text-muted-foreground">
                    ✓ {pullResult.invoices_imported} invoices, {pullResult.payments_imported} payments imported
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <OnlineOrderingCard />

        <CompanyContactCard />

        <PaymentSettingsCard />

        <PricingTypesCard />
      </div>
    </DashboardLayout>
  );
};

function PaymentSettingsCard() {
  const { data: settings, isLoading } = usePaymentSettings();
  const updateSetting = useUpdateSetting();
  const qc = useQueryClient();
  const [form, setForm] = useState<Record<string, string>>({});
  const [initialized, setInitialized] = useState(false);

  if (settings && !initialized) {
    setForm(settings);
    setInitialized(true);
  }

  const handleSave = async () => {
    // Same bounds card_surcharge_percent() applies server-side (#982): anything
    // outside them would read as "no fee" there, so refuse it here instead.
    // A plain decimal only: card_surcharge_percent() reads "1e1" or "" as 0.
    const inRange = (raw: string | undefined, max: number) =>
      raw !== undefined && /^\d+(\.\d+)?$/.test(raw.trim()) && Number(raw) <= max;
    if (!inRange(form["cc_surcharge_percent"] ?? "3", 10)) {
      toast.error("Card processing fee must be between 0 and 10%.");
      return;
    }
    if (!inRange(form["default_gratuity_percent"] ?? "0", 100)) {
      toast.error("Default gratuity must be between 0 and 100%.");
      return;
    }
    const entries = Object.entries(form);
    for (const [key, value] of entries) {
      await updateSetting.mutateAsync({ key, value });
    }
    qc.invalidateQueries({ queryKey: ["app_settings", "payment"] });
    qc.invalidateQueries({ queryKey: ["app_setting", "default_gratuity_percent"] });
    toast.success("Payment settings saved");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-xl">Payment Settings</CardTitle>
        <CardDescription>
          Configure deposit, gratuity and card fee defaults, and the e-transfer details shown to clients.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : (
          <>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Default Deposit Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={form["default_deposit_percent"] ?? "50"}
                  onChange={(e) => setForm((prev) => ({ ...prev, default_deposit_percent: e.target.value }))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">Pre-filled when generating Square payment links. Set to 100 for full payment.</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="default-gratuity-percent" className="text-sm font-medium">Default Gratuity Percentage</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="default-gratuity-percent"
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={form["default_gratuity_percent"] ?? "0"}
                  onChange={(e) => setForm((prev) => ({ ...prev, default_gratuity_percent: e.target.value }))}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">Pre-filled when you tick Gratuity on a quote. Leave at 0 for no default.</p>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Switch
                  id="cc-surcharge-enabled"
                  checked={(form["cc_surcharge_enabled"] ?? "true") === "true"}
                  onCheckedChange={(on) => setForm((prev) => ({ ...prev, cc_surcharge_enabled: on ? "true" : "false" }))}
                />
                <Label htmlFor="cc-surcharge-enabled" className="text-sm font-medium">Credit Card Processing Fee</Label>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Credit card processing fee percentage"
                  type="number"
                  min={0}
                  max={10}
                  step={0.1}
                  value={form["cc_surcharge_percent"] ?? "3"}
                  onChange={(e) => setForm((prev) => ({ ...prev, cc_surcharge_percent: e.target.value }))}
                  disabled={(form["cc_surcharge_enabled"] ?? "true") !== "true"}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-xs text-muted-foreground">Added to Square card payments only, as its own line. E-transfer totals never include it. The website terms say 3%, so update them if you change this.</p>
            </div>

            <Separator />

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">E-Transfer Email</Label>
              <Input
                type="email"
                value={form["etransfer_email"] ?? ""}
                onChange={(e) => setForm((prev) => ({ ...prev, etransfer_email: e.target.value }))}
                placeholder="payments@yourbusiness.com"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm font-medium">E-Transfer Instructions</Label>
              <Textarea
                value={form["etransfer_instructions"] ?? ""}
                onChange={(e) => setForm((prev) => ({ ...prev, etransfer_instructions: e.target.value }))}
                placeholder="e.g. Payment is auto-deposit. Once we receive your e-transfer, we will confirm with you."
              />
              <p className="text-xs text-muted-foreground">Shown to clients on the public quote page when e-transfer is selected.</p>
            </div>

            <Button onClick={handleSave} disabled={updateSetting.isPending}>
              {updateSetting.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Payment Settings
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PricingTypesCard() {
  const { data: pricingTypes, isLoading } = usePricingTypes(true);
  const createPT = useCreatePricingType();
  const updatePT = useUpdatePricingType();
  const deletePT = useDeletePricingType();
  const [adding, setAdding] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");

  const handleAdd = () => {
    const key = newKey.trim().toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
    if (!key || !newLabel.trim()) return;
    createPT.mutate(
      { key, label: newLabel.trim(), unit_label: newUnit.trim() || "qty", sort_order: (pricingTypes?.length ?? 0) },
      {
        onSuccess: () => { setNewKey(""); setNewLabel(""); setNewUnit(""); setAdding(false); toast.success("Pricing type added"); },
        onError: (e: Error) => toast.error(e.message),
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">Pricing Types</CardTitle>
            <CardDescription>Manage the pricing types available for menu items and quotes.</CardDescription>
          </div>
          <Button size="sm" variant="outline" onClick={() => setAdding(true)} disabled={adding}>
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
        ) : (
          <div className="space-y-2">
            {adding && (
              <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <Label className="text-xs">Key</Label>
                    <Input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder="per_tray" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Label</Label>
                    <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Per Tray" className="h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Unit Label</Label>
                    <Input value={newUnit} onChange={(e) => setNewUnit(e.target.value)} placeholder="trays" className="h-8 text-sm" />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewKey(""); setNewLabel(""); setNewUnit(""); }}>Cancel</Button>
                  <Button size="sm" onClick={handleAdd} disabled={createPT.isPending || !newKey.trim() || !newLabel.trim()}>
                    {createPT.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                    Add
                  </Button>
                </div>
              </div>
            )}
            {pricingTypes?.map((pt) => (
              <PricingTypeRow key={pt.key} pt={pt} onUpdate={updatePT} onDelete={deletePT} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PricingTypeRow({ pt, onUpdate, onDelete }: { pt: PricingType; onUpdate: ReturnType<typeof useUpdatePricingType>; onDelete: ReturnType<typeof useDeletePricingType> }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(pt.label);
  const [unitLabel, setUnitLabel] = useState(pt.unit_label);

  const handleSave = () => {
    onUpdate.mutate(
      { key: pt.key, label: label.trim(), unit_label: unitLabel.trim() },
      { onSuccess: () => { setEditing(false); toast.success("Updated"); }, onError: (e: Error) => toast.error(e.message) }
    );
  };

  return (
    <div className="flex items-center gap-3 border rounded-lg p-3">
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="grid grid-cols-2 gap-2">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 text-sm" placeholder="Label" />
            <Input value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} className="h-8 text-sm" placeholder="Unit label" />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{pt.label}</span>
            <Badge variant="secondary" className="text-[10px]">{pt.key}</Badge>
            <span className="text-xs text-muted-foreground">({pt.unit_label})</span>
          </div>
        )}
      </div>
      <Switch
        checked={pt.is_active}
        onCheckedChange={(checked) => onUpdate.mutate({ key: pt.key, is_active: checked })}
      />
      {editing ? (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setLabel(pt.label); setUnitLabel(pt.unit_label); }}>Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={onUpdate.isPending}>Save</Button>
        </div>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>Edit</Button>
      )}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{pt.label}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove this pricing type. Existing menu items and quotes using it will show the raw key instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => onDelete.mutate(pt.key)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default SettingsPage;
